// Sub-Step 3.3 Phase 2 — useEngagementMutation Hook.
//
// Wrapt POST /user-scrollys/:pk/like + POST /user-scrollys/:pk/save
// (Backend ist Sub-Step 3.1, deployed auf Beta 20. Mai). Beide Routes
// brauchen Bearer-Token; ohne Token → 401. Konsumenten müssen vorher
// via usePublicAuth().loggedIn checken.
//
// Optimistic-Update-Pattern:
//   1. Caller ruft toggleLike(true|false) — der gewünschte Ziel-State
//   2. Hook updated lokalen State sofort (für UI-Heart-Filled-Feedback)
//   3. POST gegen Backend
//   4. Bei Success: Backend-Response übernehmen (canonisch)
//   5. Bei Fail: Rollback zum vorherigen State + Console-Error
//
// useEffect-Audit (Phase-1-Lesson): Mutations sind ausschließlich
// caller-driven. EINE begründete Ausnahme seit Batch-2-Nachdiagnose
// (2026-06-10): der Auth-Hydration-Effect unten. Hintergrund: die
// initial*-Props kommen aus dem ANONYMEN SSR-Resolve — hasLiked/hasSaved
// sind dort nie gesetzt. Ohne authentifizierten Read-back zeigte jeder
// Reload Herz=leer, obwohl die Like-Row existiert (Bene-Abnahme-Befund;
// Writes waren 201 unter konsistenter Identität). Der Effect feuert
// EINMAL pro publicKey sobald Auth geladen ist (Deps sind reine
// Auth-/Key-Inputs, kein selbstgeschriebener State → kein Loop) und
// überschreibt NIE eine bereits laufende/erfolgte User-Mutation.

import { useCallback, useEffect, useRef, useState } from "react";
import { usePublicAuth } from "./usePublicAuth";

/**
 * Backend-Origin für API-Calls. Default ist `/` (same-origin via
 * vite-dev-proxy oder Nginx-Reverse-Proxy in Production). Override via
 * `VITE_API_URL` für Server-Side-Calls (Vike-data-loader).
 */
function apiBase(): string {
  // Client-side: same-origin via dev-proxy / Nginx — leerer prefix.
  // Server-side wird dieser Hook NIE gerufen (Browser-only via Button-Click).
  return "";
}

export type EngagementState = {
  liked: boolean;
  saved: boolean;
  likesCount: number;
  savesCount: number;
};

export interface UseEngagementMutationResult {
  /** Aktueller State (initial aus props, dann lokal optimistic + canonisch nach POST). */
  state: EngagementState;
  /** Toggle-Funktion für Like-Button. No-op wenn nicht eingeloggt. */
  toggleLike: () => Promise<void>;
  /** Toggle-Funktion für Save-Button. No-op wenn nicht eingeloggt. */
  toggleSave: () => Promise<void>;
  /** True während POST läuft (für Disable-State falls UI das nutzt). */
  isMutating: boolean;
}

interface UseEngagementMutationProps {
  publicKey: string;
  initialLiked: boolean;
  initialSaved: boolean;
  initialLikesCount: number;
  initialSavesCount: number;
}

/**
 * Backend-Response-Shape für POST /like + POST /save:
 *   `{ liked: boolean, likesCount: number }` bzw. `{ saved, savesCount }`
 */
interface ToggleLikeResponse {
  liked: boolean;
  likesCount: number;
}
interface ToggleSaveResponse {
  saved: boolean;
  savesCount: number;
}

/** Response von GET /user-scrollys/:pk/engagement (@OptionalAuth). */
interface EngagementReadResponse {
  likesCount?: number;
  savesCount?: number;
  hasLiked?: boolean;
  hasSaved?: boolean;
}

/**
 * Hook für Like/Save-Toggle mit Bearer-Token-Auth.
 *
 * @example
 * ```tsx
 * const { state, toggleLike, toggleSave } = useEngagementMutation({
 *   publicKey,
 *   initialLiked: engagement.hasLiked ?? false,
 *   initialSaved: engagement.hasSaved ?? false,
 *   initialLikesCount: engagement.likesCount,
 *   initialSavesCount: engagement.savesCount,
 * });
 * <button onClick={() => toggleLike()}>{state.liked ? "Unlike" : "Like"}</button>
 * ```
 */
export function useEngagementMutation(
  props: UseEngagementMutationProps,
): UseEngagementMutationResult {
  const auth = usePublicAuth();
  const [state, setState] = useState<EngagementState>({
    liked: props.initialLiked,
    saved: props.initialSaved,
    likesCount: props.initialLikesCount,
    savesCount: props.initialSavesCount,
  });
  const [isMutating, setIsMutating] = useState(false);
  // useRef-Guard: verhindert konkurrierende Mutations (Doppel-Klick).
  // Wenn ein POST läuft, ignoriert ein zweiter Click bis Promise resolves.
  const inFlightRef = useRef(false);
  // True sobald der User in dieser Instanz ge-toggled hat — die
  // Auth-Hydration darf danach nichts mehr überschreiben.
  const mutatedRef = useRef(false);
  // Hydration läuft einmal pro publicKey (Re-Run nur bei Key-Wechsel).
  const hydratedKeyRef = useRef<string | null>(null);

  // Auth-Hydration (Batch-2-Nachdiagnose): SSR-Resolve ist anonym →
  // hasLiked/hasSaved fehlen in den Props. Sobald der User eingeloggt
  // ist, EINMAL den @OptionalAuth-Read mit Bearer holen und den State
  // hydrieren — außer der User hat schon selbst geklickt (mutatedRef)
  // oder eine Mutation ist unterwegs (inFlightRef).
  useEffect(() => {
    if (!auth.loggedIn || !auth.accessToken) return;
    const key = props.publicKey;
    if (!key || hydratedKeyRef.current === key) return;
    hydratedKeyRef.current = key;

    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `${apiBase()}/user-scrollys/${encodeURIComponent(key)}/engagement`,
          { headers: { Authorization: `Bearer ${auth.accessToken}` } },
        );
        if (!res.ok) return; // anonym weiterzeigen ist der sichere Fallback
        const body = (await res.json()) as { data?: EngagementReadResponse };
        const d = body.data;
        if (!d || cancelled || inFlightRef.current || mutatedRef.current) {
          return;
        }
        setState((s) => ({
          liked: typeof d.hasLiked === "boolean" ? d.hasLiked : s.liked,
          saved: typeof d.hasSaved === "boolean" ? d.hasSaved : s.saved,
          likesCount:
            typeof d.likesCount === "number" ? d.likesCount : s.likesCount,
          savesCount:
            typeof d.savesCount === "number" ? d.savesCount : s.savesCount,
        }));
      } catch {
        // Netzwerk-Fail → Props-Stand bleibt (anonyme Ansicht).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.loggedIn, auth.accessToken, props.publicKey]);

  const toggleLike = useCallback(async () => {
    if (!auth.loggedIn || !auth.accessToken) return;
    if (inFlightRef.current) return;
    mutatedRef.current = true;
    inFlightRef.current = true;
    setIsMutating(true);

    // Optimistic-Update — Backup für Rollback bei Fail.
    const prevState = state;
    const targetLiked = !state.liked;
    const optimisticLikesCount = state.likesCount + (targetLiked ? 1 : -1);
    setState({
      ...state,
      liked: targetLiked,
      likesCount: Math.max(0, optimisticLikesCount),
    });

    try {
      const res = await fetch(
        `${apiBase()}/user-scrollys/${encodeURIComponent(props.publicKey)}/like`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!res.ok) {
        throw new Error(`POST /like failed: ${res.status}`);
      }
      const body = (await res.json()) as { data?: ToggleLikeResponse };
      const canonical = body.data;
      if (canonical && typeof canonical.likesCount === "number") {
        setState((s) => ({
          ...s,
          liked: canonical.liked,
          likesCount: canonical.likesCount,
        }));
      }
    } catch (err) {
      // Rollback bei Fail
      setState(prevState);
      // eslint-disable-next-line no-console
      console.error("[useEngagementMutation] toggleLike failed:", err);
    } finally {
      inFlightRef.current = false;
      setIsMutating(false);
    }
  }, [auth.loggedIn, auth.accessToken, props.publicKey, state]);

  const toggleSave = useCallback(async () => {
    if (!auth.loggedIn || !auth.accessToken) return;
    if (inFlightRef.current) return;
    mutatedRef.current = true;
    inFlightRef.current = true;
    setIsMutating(true);

    const prevState = state;
    const targetSaved = !state.saved;
    const optimisticSavesCount = state.savesCount + (targetSaved ? 1 : -1);
    setState({
      ...state,
      saved: targetSaved,
      savesCount: Math.max(0, optimisticSavesCount),
    });

    try {
      const res = await fetch(
        `${apiBase()}/user-scrollys/${encodeURIComponent(props.publicKey)}/save`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!res.ok) {
        throw new Error(`POST /save failed: ${res.status}`);
      }
      const body = (await res.json()) as { data?: ToggleSaveResponse };
      const canonical = body.data;
      if (canonical && typeof canonical.savesCount === "number") {
        setState((s) => ({
          ...s,
          saved: canonical.saved,
          savesCount: canonical.savesCount,
        }));
      }
    } catch (err) {
      setState(prevState);
      // eslint-disable-next-line no-console
      console.error("[useEngagementMutation] toggleSave failed:", err);
    } finally {
      inFlightRef.current = false;
      setIsMutating(false);
    }
  }, [auth.loggedIn, auth.accessToken, props.publicKey, state]);

  return { state, toggleLike, toggleSave, isMutating };
}
